import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Ticket, Users, Copy, Check, Inbox, Mail, Download, AlertCircle, Info, DollarSign, TrendingUp, Zap, CreditCard, Bug, Plug, X, ExternalLink } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { FOUNDING_CUSTOMER_LETTER } from "@/lib/foundingLetter";

/**
 * Platform Owner Console — visible only to users with role='platform_owner'.
 * Phase 0: companies + VIP coupons. Sprint D adds the Demo Requests tab.
 */
export default function PlatformConsole() {
    const { user } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [demoRequests, setDemoRequests] = useState([]);
    const [demoStats, setDemoStats] = useState({ total: 0, new: 0, scheduled_this_week: 0 });
    const [legalInquiries, setLegalInquiries] = useState([]);
    const [legalInquiriesStats, setLegalInquiriesStats] = useState({ total: 0, new: 0, scheduled: 0, closed_won: 0 });
    const [legalInquiriesError, setLegalInquiriesError] = useState(null);
    const [legalUpdatingId, setLegalUpdatingId] = useState(null);
    const [emailStatus, setEmailStatus] = useState(null); // {configured, sender_email, last_successful_test_at?}
    const [testingEmail, setTestingEmail] = useState(false);
    const [emailTestResult, setEmailTestResult] = useState(null); // "ok" | "fail" | null
    const [emailEvents, setEmailEvents] = useState(null); // Sprint G G-2
    const [stripeStatus, setStripeStatus] = useState(null); // Sprint G G-1
    const [sentryStatus, setSentryStatus] = useState(null); // Sprint G G-6
    const [oauthStatus, setOauthStatus] = useState(null); // Sprint G G-3
    const [usage, setUsage] = useState(null); // Sprint G G-7
    const [usageBusy, setUsageBusy] = useState(false);
    const [showFoundingModal, setShowFoundingModal] = useState(false); // Sprint G G-8
    const [copiedLetter, setCopiedLetter] = useState(false);
    const [loading, setLoading] = useState(true);
    const [copiedCode, setCopiedCode] = useState(null);
    const [err, setErr] = useState(null);
    const [updatingId, setUpdatingId] = useState(null);

    const loadDemoRequests = async () => {
        try {
            const r = await api.get("/platform/demo-requests");
            setDemoRequests(r.data.demo_requests || []);
            setDemoStats(r.data.stats || {});
        } catch {}
    };

    const loadLegalInquiries = async () => {
        try {
            const r = await api.get("/platform/legal-enterprise-inquiries");
            setLegalInquiries(r.data.inquiries || []);
            setLegalInquiriesStats(r.data.stats || {});
            setLegalInquiriesError(null);
        } catch (e) {
            setLegalInquiriesError(
                e?.response?.data?.detail || e?.message || "Could not load Legal Enterprise inquiries."
            );
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const [c, co, d, es, stripe, sentry, oauth, ee] = await Promise.all([
                    api.get("/platform/companies"),
                    api.get("/platform/coupons"),
                    api.get("/platform/demo-requests"),
                    api.get("/platform/email/status").catch(() => ({ data: null })),
                    api.get("/platform/stripe/status").catch(() => ({ data: null })),
                    api.get("/platform/sentry/status").catch(() => ({ data: null })),
                    api.get("/platform/integrations/oauth-status").catch(() => ({ data: null })),
                    api.get("/platform/email/events?limit=6").catch(() => ({ data: null })),
                ]);
                setCompanies(c.data.companies || []);
                setCoupons(co.data.coupons || []);
                setDemoRequests(d.data.demo_requests || []);
                setDemoStats(d.data.stats || {});
                setEmailStatus(es.data);
                setStripeStatus(stripe.data);
                setSentryStatus(sentry.data);
                setOauthStatus(oauth.data);
                setEmailEvents(ee.data);
                // Route the initial Legal Enterprise pipeline load through the
                // dedicated loader so a 5xx surfaces `legalInquiriesError` in
                // the banner instead of silently falling back to empty data.
                await loadLegalInquiries();
                await loadUsage();
            } catch (e) {
                setErr(e?.response?.data?.detail || "Access denied");
            } finally {
                setLoading(false);
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadUsage = async () => {
        setUsageBusy(true);
        try {
            const r = await api.get("/platform/usage/overview");
            setUsage(r.data);
        } catch { /* owner only — silent */ }
        setUsageBusy(false);
    };

    const copyLetter = async () => {
        try {
            await navigator.clipboard.writeText(FOUNDING_CUSTOMER_LETTER);
            setCopiedLetter(true);
            setTimeout(() => setCopiedLetter(false), 2000);
        } catch { /* clipboard not permitted */ }
    };

    const testEmail = async () => {
        setTestingEmail(true);
        setEmailTestResult(null);
        try {
            // Sprint G: use the test-send alias so an email_send_events row is
            // written for both success and failure paths.
            const r = await api.post("/platform/email/test-send");
            setEmailTestResult(r.data?.ok ? "ok" : "fail");
            // Refresh status + events history so the Delivery panel updates
            // immediately after the click.
            const [es, ee] = await Promise.all([
                api.get("/platform/email/status"),
                api.get("/platform/email/events?limit=6").catch(() => ({ data: null })),
            ]);
            setEmailStatus(es.data);
            if (ee.data) setEmailEvents(ee.data);
        } catch {
            setEmailTestResult("fail");
        }
        setTestingEmail(false);
    };

    const copy = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedCode(code);
            setTimeout(() => setCopiedCode(null), 1500);
        } catch {}
    };

    const setStatus = async (id, status) => {
        setUpdatingId(id);
        try {
            await api.patch(`/platform/demo-requests/${id}`, { status });
            await loadDemoRequests();
        } catch {}
        setUpdatingId(null);
    };

    const setLegalInquiryStatus = async (id, status) => {
        setLegalUpdatingId(id);
        try {
            await api.patch(`/platform/legal-enterprise-inquiries/${id}`, { status });
            await loadLegalInquiries();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Update failed.";
            setLegalInquiriesError(typeof msg === "string" ? msg : "Update failed.");
        }
        setLegalUpdatingId(null);
    };

    const downloadCsv = async () => {
        try {
            const token = localStorage.getItem("wfos_token");
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/platform/demo-requests/export.csv`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = "wfos-demo-requests.csv";
            a.click();
            URL.revokeObjectURL(objectUrl);
        } catch {}
    };

    if (user?.role !== "platform_owner") {
        return (
            <div className="h-full grid place-items-center text-sm text-gray-500">
                <div className="wfos-card p-8 text-center">
                    <Shield className="w-8 h-8 mx-auto text-gray-400" strokeWidth={1.5} />
                    <div className="mt-3 text-gray-900 font-medium">Platform owner only.</div>
                    <div className="mt-1 text-gray-500 text-xs">Return to <Link to="/app" className="text-[#16a34a]">Home</Link></div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto wfos-scrollbar p-8">
            <div className="max-w-6xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-amber-800">
                    <Shield className="w-3 h-3" strokeWidth={2} /> Platform Owner Console
                </div>
                <h1 className="mt-3 text-3xl font-medium tracking-tight text-gray-900">
                    Matcom Operations · god-mode
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                    Cross-tenant visibility and VIP coupon inventory. Full admin UI ships in a later phase.
                </p>

                {err && <div className="mt-6 wfos-card p-4 text-sm text-red-600">{err}</div>}
                {loading && <div className="mt-6 text-sm text-gray-500">Loading…</div>}

                {!loading && !err && (
                    <>
                        <section className="mt-8">
                            <div className="flex items-center gap-2 mb-3">
                                <Users className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                                <div className="text-sm font-semibold text-gray-900">All companies ({companies.length})</div>
                            </div>
                            <div className="wfos-card overflow-hidden">
                                <table className="w-full text-sm" data-testid="platform-companies-table">
                                    <thead className="bg-[#fafaf7] text-[11px] uppercase tracking-widest text-gray-500">
                                        <tr>
                                            <th className="text-left px-4 py-2 font-medium">Company</th>
                                            <th className="text-left px-4 py-2 font-medium">Slug</th>
                                            <th className="text-left px-4 py-2 font-medium">Plan</th>
                                            <th className="text-right px-4 py-2 font-medium">Users</th>
                                            <th className="text-right px-4 py-2 font-medium">Hired</th>
                                            <th className="text-left px-4 py-2 font-medium">Created</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#e8e8e3]">
                                        {companies.map((c) => (
                                            <tr key={c.id} data-testid={`platform-company-${c.id}`}>
                                                <td className="px-4 py-2 text-gray-900 font-medium">
                                                    {c.name}
                                                    {c.is_owner_workspace && (
                                                        <span className="ml-2 wfos-pill text-[9px]">OWNER</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-gray-500 font-mono text-xs">{c.slug}</td>
                                                <td className="px-4 py-2">
                                                    <span className="wfos-pill">{c.plan}</span>
                                                </td>
                                                <td className="px-4 py-2 text-right tabular-nums">{c.user_count}</td>
                                                <td className="px-4 py-2 text-right tabular-nums">{c.hired_count}</td>
                                                <td className="px-4 py-2 text-gray-500 text-xs">
                                                    {new Date(c.created_at).toLocaleDateString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        <section className="mt-10">
                            <div className="flex items-center gap-2 mb-3">
                                <Ticket className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                                <div className="text-sm font-semibold text-gray-900">VIP Coupons ({coupons.length})</div>
                                <button
                                    onClick={() => setShowFoundingModal(true)}
                                    data-testid="founding-letter-open"
                                    className="ml-auto inline-flex items-center gap-1 rounded-md border border-[#e8e8e3] bg-white px-2.5 h-7 text-[11.5px] font-medium text-gray-800 hover:border-[#d6d6cf]"
                                >
                                    <Mail className="w-3 h-3" strokeWidth={2} /> Founding customer offer template
                                </button>
                            </div>
                            <div className="text-[11.5px] text-gray-500 mb-2">
                                Assign codes to prospects as they say yes — each code is single-use lifetime access.
                            </div>
                            <div className="wfos-card overflow-hidden">
                                <table className="w-full text-sm" data-testid="platform-coupons-table">
                                    <thead className="bg-[#fafaf7] text-[11px] uppercase tracking-widest text-gray-500">
                                        <tr>
                                            <th className="text-left px-4 py-2 font-medium">Label</th>
                                            <th className="text-left px-4 py-2 font-medium">Code</th>
                                            <th className="text-left px-4 py-2 font-medium">Grants</th>
                                            <th className="text-left px-4 py-2 font-medium">Status</th>
                                            <th className="text-right px-4 py-2 font-medium"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#e8e8e3]">
                                        {coupons.map((c) => {
                                            const redeemed = c.redeemed_count > 0;
                                            return (
                                                <tr key={c.id} data-testid={`platform-coupon-${c.code}`}>
                                                    <td className="px-4 py-2 font-medium text-gray-900">{c.label}</td>
                                                    <td className="px-4 py-2 font-mono text-xs text-gray-800">{c.code}</td>
                                                    <td className="px-4 py-2 text-gray-600 text-xs">{c.grants_plan}</td>
                                                    <td className="px-4 py-2">
                                                        {redeemed ? (
                                                            <span className="wfos-pill">Redeemed {c.redeemed_at ? new Date(c.redeemed_at).toLocaleDateString() : ""}</span>
                                                        ) : (
                                                            <span className="wfos-pill wfos-pill--green">Available</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 text-right">
                                                        {!redeemed && (
                                                            <button
                                                                onClick={() => copy(c.code)}
                                                                className="inline-flex items-center gap-1 rounded-md border border-[#e8e8e3] px-2 h-7 text-xs text-gray-700 hover:border-[#d6d6cf]"
                                                            >
                                                                {copiedCode === c.code ? <Check className="w-3 h-3 text-[#16a34a]" strokeWidth={2} /> : <Copy className="w-3 h-3" strokeWidth={2} />}
                                                                {copiedCode === c.code ? "Copied" : "Copy"}
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-3 text-[11px] text-gray-400">
                                Full status also mirrored to <code>/app/memory/vip_coupons.md</code>.
                            </div>
                        </section>

                        <section className="mt-10" data-testid="demo-requests-section">
                            {/* Sprint E — Email delivery setup banner */}
                            <EmailSetupBanner
                                status={emailStatus}
                                testing={testingEmail}
                                lastTestResult={emailTestResult}
                                onTest={testEmail}
                            />

                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Inbox className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                                    <div className="text-sm font-semibold text-gray-900">Demo Requests ({demoStats.total})</div>
                                    <span className="wfos-pill wfos-pill--green text-[9px]">{demoStats.new || 0} new</span>
                                    <span className="wfos-pill text-[9px]">{demoStats.scheduled_this_week || 0} scheduled this week</span>
                                </div>
                                <button
                                    onClick={downloadCsv}
                                    data-testid="demo-requests-export"
                                    className="inline-flex items-center gap-1 rounded-md border border-[#e8e8e3] bg-white px-3 h-8 text-xs text-gray-800 hover:border-[#d6d6cf]"
                                >
                                    <Download className="w-3 h-3" strokeWidth={2} /> Export CSV
                                </button>
                            </div>

                            {demoRequests.length === 0 ? (
                                <div className="wfos-card p-6 text-center text-sm text-gray-500">
                                    <Mail className="w-6 h-6 mx-auto text-gray-300" strokeWidth={1.5} />
                                    <div className="mt-2">No demo requests yet.</div>
                                </div>
                            ) : (
                                <div className="wfos-card overflow-hidden">
                                    <table className="w-full text-sm" data-testid="demo-requests-table">
                                        <thead className="bg-[#fafaf7] text-[11px] uppercase tracking-widest text-gray-500">
                                            <tr>
                                                <th className="text-left px-4 py-2 font-medium">Submitted</th>
                                                <th className="text-left px-4 py-2 font-medium">Name / Company</th>
                                                <th className="text-left px-4 py-2 font-medium">Email</th>
                                                <th className="text-left px-4 py-2 font-medium">Size</th>
                                                <th className="text-left px-4 py-2 font-medium">Interest</th>
                                                <th className="text-left px-4 py-2 font-medium">Status</th>
                                                <th className="text-left px-4 py-2 font-medium">Email sent</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-[#e8e8e3]">
                                            {demoRequests.map((r) => (
                                                <tr key={r.id} data-testid={`demo-req-${r.id}`}>
                                                    <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap">
                                                        {new Date(r.submitted_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <div className="text-gray-900 font-medium">{r.full_name}</div>
                                                        <div className="text-gray-500 text-xs">{r.company_name}</div>
                                                    </td>
                                                    <td className="px-4 py-2 text-gray-600 text-xs">
                                                        <a href={`mailto:${r.work_email}`} className="hover:text-[#16a34a]">{r.work_email}</a>
                                                    </td>
                                                    <td className="px-4 py-2 text-gray-500 text-xs">{r.company_size || "—"}</td>
                                                    <td className="px-4 py-2 text-gray-500 text-xs">{r.interest || "General"}</td>
                                                    <td className="px-4 py-2">
                                                        <select
                                                            value={r.status}
                                                            disabled={updatingId === r.id}
                                                            onChange={(e) => setStatus(r.id, e.target.value)}
                                                            data-testid={`demo-req-status-${r.id}`}
                                                            className={`text-xs rounded-md border px-2 h-7 ${
                                                                r.status === "new" ? "bg-[#f0fdf4] border-[#bbf7d0] text-[#166534]" :
                                                                r.status === "scheduled" ? "bg-blue-50 border-blue-200 text-blue-800" :
                                                                r.status === "closed" || r.status === "spam" ? "bg-gray-50 border-gray-200 text-gray-500" :
                                                                "bg-amber-50 border-amber-200 text-amber-800"
                                                            }`}
                                                        >
                                                            <option value="new">New</option>
                                                            <option value="contacted">Contacted</option>
                                                            <option value="scheduled">Scheduled</option>
                                                            <option value="closed">Closed</option>
                                                            <option value="spam">Spam</option>
                                                        </select>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {r.notification_sent ? (
                                                            <span className="wfos-pill wfos-pill--green text-[9px]">
                                                                <Check className="w-2.5 h-2.5" strokeWidth={2.5} /> Sent
                                                            </span>
                                                        ) : (
                                                            <span
                                                                className="wfos-pill text-[9px]"
                                                                title={r.notification_error || "email not configured"}
                                                            >
                                                                <AlertCircle className="w-2.5 h-2.5" strokeWidth={2} /> Not sent
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <div className="mt-2 text-[11px] text-gray-400">
                                Email delivery is optional. If <code>RESEND_API_KEY</code> is unset, requests are still stored here — no lead lost.
                            </div>
                        </section>

                        {/* Phase 4a polish — Legal Enterprise Pipeline */}
                        <LegalEnterprisePipeline
                            inquiries={legalInquiries}
                            stats={legalInquiriesStats}
                            onStatusChange={setLegalInquiryStatus}
                            updatingId={legalUpdatingId}
                            error={legalInquiriesError}
                        />

                        {/* Sprint G G-7 — Usage & Spend */}
                        <UsageSection usage={usage} onRefresh={loadUsage} busy={usageBusy} />

                        {/* Sprint G G-1 — Stripe setup */}
                        <StripeSetupPanel status={stripeStatus} />

                        {/* Sprint G G-3 — OAuth integrations status */}
                        <OAuthStatusPanel status={oauthStatus} />

                        {/* Sprint G G-6 — Sentry setup */}
                        <SentrySetupPanel status={sentryStatus} />

                        {/* Sprint G G-2 — Email events (delivery history) */}
                        <EmailEventsPanel events={emailEvents} />
                    </>
                )}
            </div>

            {/* Sprint G G-8 — Founding-customer letter modal */}
            {showFoundingModal && (
                <FoundingLetterModal
                    onClose={() => setShowFoundingModal(false)}
                    onCopy={copyLetter}
                    copied={copiedLetter}
                />
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------
 * Sprint E — Email delivery setup banner
 * Renders inside the Owner-only Demo Requests section. NEVER exposes the
 * owner's notification email to non-owners (this whole panel is gated).
 * ------------------------------------------------------------------------- */
function EmailSetupBanner({ status, testing, lastTestResult, onTest }) {
    if (!status) return null;
    const configured = status.configured;
    const lastAt = status.last_successful_test_at;

    if (configured && lastAt) {
        return (
            <div
                data-testid="email-setup-banner"
                data-state="active"
                className="mb-4 rounded-xl border border-[#bbf7d0] bg-[#f0fdf4] p-4 flex items-center gap-3"
            >
                <span className="w-8 h-8 rounded-full bg-white grid place-items-center flex-shrink-0">
                    <Check className="w-4 h-4 text-[#16a34a]" strokeWidth={2.5} />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold text-gray-900">Email delivery is active</div>
                    <div className="text-[12px] text-gray-600 mt-0.5">
                        Sender: <code>{status.sender_email}</code> · Last successful send{" "}
                        {new Date(lastAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                </div>
                <button
                    onClick={onTest}
                    disabled={testing}
                    data-testid="email-test-btn"
                    className="rounded-md border border-[#e8e8e3] bg-white px-3 h-8 text-xs font-medium text-gray-800 hover:border-[#d6d6cf] disabled:opacity-50"
                >
                    {testing ? "Testing…" : "Test again"}
                </button>
            </div>
        );
    }

    return (
        <div
            data-testid="email-setup-banner"
            data-state="unconfigured"
            className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-5"
        >
            <div className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-full bg-white grid place-items-center flex-shrink-0 mt-0.5">
                    <AlertCircle className="w-4 h-4 text-amber-600" strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-[14.5px] font-semibold text-gray-900">Email delivery is not configured</div>
                    <p className="mt-1 text-[12.5px] text-gray-700 leading-relaxed">
                        Demo requests are being captured here, but no email notification is being sent to your inbox. To enable email delivery, set the <code className="text-[11px] bg-white/70 rounded px-1 py-0.5">RESEND_API_KEY</code> environment variable in your backend.
                    </p>
                    <ol className="mt-3 space-y-2 text-[12.5px] text-gray-800 list-decimal list-inside">
                        <li>
                            <b>Get a Resend API key</b> — Free tier at{" "}
                            <a href="https://resend.com/signup" target="_blank" rel="noreferrer" className="text-[#16a34a] font-medium hover:underline">
                                resend.com/signup
                            </a>{" "}
                            (100 emails/day forever). Copy your API key from the dashboard.
                        </li>
                        <li>
                            <b>Add it to backend <code className="text-[11px] bg-white/70 rounded px-1 py-0.5">.env</code></b> — Open <code className="text-[11px] bg-white/70 rounded px-1 py-0.5">/app/backend/.env</code> and add:
                            <div className="mt-1 rounded-md bg-[#0b0b0c] text-[#a7f3d0] text-[11px] font-mono px-3 py-2">RESEND_API_KEY=re_xxxxxxxxxxxxxxxxxxxx</div>
                        </li>
                        <li>
                            <b>Restart the backend</b> — Run{" "}
                            <code className="text-[11px] bg-white/70 rounded px-1 py-0.5">sudo supervisorctl restart backend</code>, then test below.
                        </li>
                    </ol>
                    <div className="mt-4 flex items-center gap-3">
                        <button
                            onClick={onTest}
                            disabled={testing}
                            data-testid="email-test-btn"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 px-3 h-9 text-xs font-semibold text-white disabled:opacity-50"
                        >
                            <Mail className="w-3.5 h-3.5" strokeWidth={2} />
                            {testing ? "Sending test…" : "Test email delivery"}
                        </button>
                        {lastTestResult === "fail" && (
                            <span className="inline-flex items-center gap-1 text-[11.5px] text-red-700">
                                <AlertCircle className="w-3 h-3" strokeWidth={2} /> Test failed — check backend logs.
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 ml-auto">
                            <Info className="w-3 h-3" strokeWidth={2} /> Fallback active — requests still stored below.
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}



/* -------------------------------------------------------------------------
 * Sprint G G-7 — Usage & Spend
 * ------------------------------------------------------------------------- */
function UsageSection({ usage, onRefresh, busy }) {
    if (!usage) {
        return (
            <section className="mt-10" data-testid="usage-section-loading">
                <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                    <div className="text-sm font-semibold text-gray-900">Usage & Spend</div>
                </div>
                <div className="wfos-card p-8 text-sm text-gray-400 text-center">Loading usage data…</div>
            </section>
        );
    }
    const highSpenders = (usage.by_tenant || []).filter((t) => (t.spend_pct_of_cap || 0) >= 80);
    return (
        <section className="mt-10" data-testid="usage-section">
            <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                <div className="text-sm font-semibold text-gray-900">Usage & Spend</div>
                {highSpenders.length > 0 && (
                    <span className="wfos-pill text-[9px] bg-amber-50 text-amber-800 border border-amber-200" data-testid="usage-highspender-count">
                        {highSpenders.length} tenants over 80% of cap
                    </span>
                )}
                <button
                    onClick={onRefresh}
                    disabled={busy}
                    data-testid="usage-refresh"
                    className="ml-auto inline-flex items-center gap-1 rounded-md border border-[#e8e8e3] bg-white px-2.5 h-7 text-[11.5px] text-gray-700 hover:border-[#d6d6cf] disabled:opacity-50"
                >
                    {busy ? "…" : "Refresh"}
                </button>
            </div>

            {/* KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <UsageKPI label="Calls today"  value={usage.total_calls_today.toLocaleString()} icon={<Zap className="w-4 h-4 text-[#16a34a]" strokeWidth={2} />} testid="usage-kpi-calls-today" />
                <UsageKPI label="Tokens today" value={usage.total_tokens_today.toLocaleString()} icon={<TrendingUp className="w-4 h-4 text-[#16a34a]" strokeWidth={2} />} testid="usage-kpi-tokens-today" />
                <UsageKPI label="Cost today"    value={`$${usage.total_cost_today_usd.toFixed(2)}`} icon={<DollarSign className="w-4 h-4 text-[#16a34a]" strokeWidth={2} />} testid="usage-kpi-cost-today" />
                <UsageKPI label="Cost MTD"       value={`$${usage.total_cost_month_usd.toFixed(2)}`} icon={<DollarSign className="w-4 h-4 text-[#16a34a]" strokeWidth={2} />} testid="usage-kpi-cost-month" />
            </div>

            {/* Trend chart */}
            {usage.trend_7d?.length > 0 && (
                <div className="wfos-card p-4 mt-3" data-testid="usage-trend-chart">
                    <div className="wfos-label text-gray-500 mb-2">7-day cost trend</div>
                    <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={usage.trend_7d}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e3" />
                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6b7280" }} tickFormatter={(d) => d.slice(5)} />
                                <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} tickFormatter={(v) => `$${v.toFixed(1)}`} width={45} />
                                <Tooltip
                                    contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e8e8e3" }}
                                    formatter={(v) => [`$${(+v).toFixed(4)}`, "Cost"]}
                                />
                                <Line type="monotone" dataKey="cost_usd" stroke="#16a34a" strokeWidth={2} dot={{ r: 2.5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* By worker */}
            <div className="mt-4">
                <div className="text-[12px] font-semibold text-gray-700 mb-2">Top workers by cost (today)</div>
                {usage.by_worker?.length ? (
                    <div className="wfos-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="usage-by-worker-table">
                            <thead className="bg-[#fafaf7] text-[11px] uppercase tracking-widest text-gray-500">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">Worker</th>
                                    <th className="text-right px-4 py-2 font-medium">Calls</th>
                                    <th className="text-right px-4 py-2 font-medium">Tokens</th>
                                    <th className="text-right px-4 py-2 font-medium">Cost</th>
                                    <th className="text-right px-4 py-2 font-medium">$/call</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#e8e8e3]">
                                {usage.by_worker.map((w) => (
                                    <tr key={w.worker_slug} data-testid={`usage-worker-${w.worker_slug}`}>
                                        <td className="px-4 py-2 font-medium text-gray-900">{w.worker_name}</td>
                                        <td className="px-4 py-2 text-right tabular-nums">{w.calls}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-gray-600">{(w.tokens_in + w.tokens_out).toLocaleString()}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-medium">${w.cost_usd.toFixed(4)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums text-gray-500 text-xs">${(w.cost_usd / Math.max(1, w.calls)).toFixed(4)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="wfos-card p-4 text-center text-sm text-gray-400">No worker activity yet today.</div>
                )}
            </div>

            {/* By tenant */}
            <div className="mt-4">
                <div className="text-[12px] font-semibold text-gray-700 mb-2">Top tenants by spend (today)</div>
                {usage.by_tenant?.length ? (
                    <div className="wfos-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="usage-by-tenant-table">
                            <thead className="bg-[#fafaf7] text-[11px] uppercase tracking-widest text-gray-500">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">Company</th>
                                    <th className="text-left px-4 py-2 font-medium">Plan</th>
                                    <th className="text-right px-4 py-2 font-medium">Calls</th>
                                    <th className="text-right px-4 py-2 font-medium">Cost</th>
                                    <th className="text-left px-4 py-2 font-medium">% of daily cap</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#e8e8e3]">
                                {usage.by_tenant.map((t) => {
                                    const pct = t.spend_pct_of_cap ?? 0;
                                    const warn = pct >= 80;
                                    return (
                                        <tr key={t.company_id} data-testid={`usage-tenant-${t.company_id}`}>
                                            <td className="px-4 py-2 font-medium text-gray-900">
                                                {t.company_name}
                                                {warn && (
                                                    <AlertCircle className="inline-block ml-1.5 w-3.5 h-3.5 text-amber-600" strokeWidth={2} />
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-gray-600 text-xs">{t.plan}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{t.calls}</td>
                                            <td className="px-4 py-2 text-right tabular-nums font-medium">${t.cost_usd.toFixed(4)}</td>
                                            <td className="px-4 py-2">
                                                {t.spend_cap_usd ? (
                                                    <div className="flex items-center gap-2 min-w-[120px]">
                                                        <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                                            <div
                                                                className={`h-full ${warn ? "bg-amber-500" : "bg-[#16a34a]"}`}
                                                                style={{ width: `${Math.min(100, pct)}%` }}
                                                            />
                                                        </div>
                                                        <span className={`text-[11px] tabular-nums ${warn ? "text-amber-800 font-semibold" : "text-gray-500"}`}>{pct.toFixed(0)}%</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-[11px] text-emerald-700 font-semibold">UNLIMITED</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="wfos-card p-4 text-center text-sm text-gray-400">No tenant spend yet today.</div>
                )}
            </div>
        </section>
    );
}

function UsageKPI({ label, value, icon, testid }) {
    return (
        <div className="wfos-card p-4" data-testid={testid}>
            <div className="wfos-label text-gray-500 inline-flex items-center gap-1.5">{icon}{label}</div>
            <div className="mt-2 text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
        </div>
    );
}

/* -------------------------------------------------------------------------
 * Sprint G G-1 — Stripe setup panel
 * ------------------------------------------------------------------------- */
function StripeSetupPanel({ status }) {
    if (!status) return null;
    return (
        <section className="mt-10" data-testid="stripe-setup-panel">
            <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                <div className="text-sm font-semibold text-gray-900">Stripe Billing</div>
                {status.configured ? (
                    <span className="wfos-pill wfos-pill--green text-[9px]">CONFIGURED</span>
                ) : (
                    <span className="wfos-pill text-[9px] bg-amber-50 text-amber-800 border border-amber-200">NOT CONFIGURED</span>
                )}
            </div>
            {status.configured ? (
                <div className="wfos-card p-5 text-[13px] text-gray-700 leading-relaxed">
                    Stripe is configured. Manage products, prices, and webhooks in your Stripe dashboard. Users see live checkout on <code>/pricing</code> and the Manage Billing button on <code>/app/billing</code>.
                </div>
            ) : (
                <div className="wfos-card p-5">
                    <div className="text-[14px] font-semibold text-gray-900">Set up Stripe (4 steps)</div>
                    <p className="mt-1 text-[12.5px] text-gray-600 leading-relaxed">
                        With Stripe env unset, checkout endpoints return "not configured" and the pricing page falls back to a Contact-Sales flow. Configure to enable live billing.
                    </p>
                    <ol className="mt-4 space-y-3 text-[12.5px] text-gray-800 list-decimal list-inside">
                        <li>
                            <b>Create a Stripe account</b> —{" "}
                            <a href="https://dashboard.stripe.com/register" target="_blank" rel="noreferrer" className="text-[#16a34a] hover:underline">stripe.com/register</a>. Grab a test-mode secret key from Dashboard → Developers → API keys.
                        </li>
                        <li>
                            <b>Create 4 recurring prices</b> with these lookup_keys (used by the checkout code):
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                                {Object.entries(status.lookup_keys || {}).map(([plan, ints]) => (
                                    <div key={plan} className="rounded-md border border-[#e8e8e3] p-2">
                                        <div className="font-semibold text-gray-800 uppercase tracking-widest text-[9.5px]">{plan}</div>
                                        <div className="mt-1 font-mono text-[10.5px] text-gray-600">
                                            {ints.monthly} · {ints.yearly}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </li>
                        <li>
                            <b>Add keys to backend .env</b>:
                            <div className="mt-1 rounded-md bg-[#0b0b0c] text-[#a7f3d0] text-[11px] font-mono px-3 py-2 whitespace-pre">
STRIPE_SECRET_KEY=sk_test_xxxxxxxxxxxxxxx
STRIPE_WEBHOOK_SECRET=whsec_xxxxxxxxxxxxxxx</div>
                        </li>
                        <li>
                            <b>Test the webhook</b> — install{" "}
                            <a href="https://stripe.com/docs/stripe-cli" target="_blank" rel="noreferrer" className="text-[#16a34a] hover:underline">Stripe CLI</a>, then:
                            <div className="mt-1 rounded-md bg-[#0b0b0c] text-[#a7f3d0] text-[11px] font-mono px-3 py-2">stripe listen --forward-to localhost:8001/api/billing/webhook</div>
                            Restart backend: <code className="text-[10.5px] bg-white/70 rounded px-1 py-0.5">sudo supervisorctl restart backend</code>
                        </li>
                    </ol>
                    <div className="mt-4 text-[11px] text-gray-400">
                        Missing env: {(status.missing_env || []).map((e) => <code key={e} className="mr-1 bg-white/60 px-1 py-0.5 rounded">{e}</code>)}
                    </div>
                </div>
            )}
        </section>
    );
}

/* -------------------------------------------------------------------------
 * Sprint G G-3 — Real-OAuth integrations status panel
 * ------------------------------------------------------------------------- */
function OAuthStatusPanel({ status }) {
    if (!status) return null;
    const providers = status.providers || [];
    return (
        <section className="mt-10" data-testid="oauth-setup-panel">
            <div className="flex items-center gap-2 mb-3">
                <Plug className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                <div className="text-sm font-semibold text-gray-900">Integrations & OAuth</div>
                <span className="wfos-pill text-[9px]">Top 5 real · rest mock</span>
            </div>
            <div className="wfos-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#fafaf7] text-[11px] uppercase tracking-widest text-gray-500">
                        <tr>
                            <th className="text-left px-4 py-2 font-medium">Integration</th>
                            <th className="text-left px-4 py-2 font-medium">Status</th>
                            <th className="text-left px-4 py-2 font-medium">Missing env</th>
                            <th className="text-left px-4 py-2 font-medium">Docs</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e8e8e3]">
                        {providers.map((p) => {
                            const missing = (p.missing_env || []).filter(Boolean);
                            return (
                                <tr key={p.slug} data-testid={`oauth-row-${p.slug}`}>
                                    <td className="px-4 py-2 font-medium text-gray-900 capitalize">{p.slug.replace("-", " ")}</td>
                                    <td className="px-4 py-2">
                                        {p.configured ? (
                                            <span className="wfos-pill wfos-pill--green text-[9px]">LIVE</span>
                                        ) : (
                                            <span className="wfos-pill text-[9px] bg-gray-50 text-gray-700 border border-gray-200">Mock mode</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-[11px] font-mono text-gray-500">
                                        {missing.length ? missing.join(", ") : <span className="text-emerald-700">—</span>}
                                    </td>
                                    <td className="px-4 py-2 text-[11px]">
                                        <a href={OAUTH_DOCS[p.slug]} target="_blank" rel="noreferrer" className="text-[#16a34a] hover:underline inline-flex items-center gap-1">
                                            Setup <ExternalLink className="w-3 h-3" strokeWidth={2} />
                                        </a>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="mt-2 text-[11px] text-gray-500">
                Set each provider's <code>CLIENT_ID</code> + <code>CLIENT_SECRET</code> env vars to flip that integration from mock to real OAuth. All others stay on the mock flow.
            </div>
        </section>
    );
}

const OAUTH_DOCS = {
    "google-workspace": "https://console.cloud.google.com/apis/credentials",
    "slack":            "https://api.slack.com/apps",
    "hubspot":          "https://developers.hubspot.com/docs/api/oauth-quickstart-guide",
    "salesforce":       "https://help.salesforce.com/s/articleView?id=sf.connected_app_create.htm",
    "notion":           "https://developers.notion.com/docs/authorization",
};

/* -------------------------------------------------------------------------
 * Sprint G G-6 — Sentry setup panel
 * ------------------------------------------------------------------------- */
function SentrySetupPanel({ status }) {
    if (!status) return null;
    return (
        <section className="mt-10" data-testid="sentry-setup-panel">
            <div className="flex items-center gap-2 mb-3">
                <Bug className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                <div className="text-sm font-semibold text-gray-900">Sentry Error Monitoring</div>
                {status.active_now ? (
                    <span className="wfos-pill wfos-pill--green text-[9px]">ACTIVE</span>
                ) : (
                    <span className="wfos-pill text-[9px] bg-amber-50 text-amber-800 border border-amber-200">NOT CONFIGURED</span>
                )}
            </div>
            {status.active_now ? (
                <div className="wfos-card p-5 text-[13px] text-gray-700 leading-relaxed">
                    Sentry backend integration is active. Environment: <code>{status.environment}</code>. Frontend DSN is {status.frontend_configured ? "configured" : "not yet configured"}.
                </div>
            ) : (
                <div className="wfos-card p-5">
                    <div className="text-[14px] font-semibold text-gray-900">Wire up Sentry</div>
                    <p className="mt-1 text-[12.5px] text-gray-600 leading-relaxed">
                        Errors are captured locally today. Add Sentry DSNs to route unhandled exceptions + slow transactions to your Sentry project.
                    </p>
                    <ol className="mt-3 space-y-2 text-[12.5px] text-gray-800 list-decimal list-inside">
                        <li>Sign up at <a href="https://sentry.io/signup/" target="_blank" rel="noreferrer" className="text-[#16a34a] hover:underline">sentry.io</a> (free tier is generous).</li>
                        <li>Create a <b>Python (FastAPI)</b> project — copy the DSN.</li>
                        <li>Create a <b>React</b> project — copy the second DSN.</li>
                        <li>
                            Add to env:
                            <div className="mt-1 rounded-md bg-[#0b0b0c] text-[#a7f3d0] text-[11px] font-mono px-3 py-2 whitespace-pre">
Backend .env:
SENTRY_DSN_BACKEND=https://xxxxxxxx@sentry.io/xxxxx

Frontend .env:
REACT_APP_SENTRY_DSN=https://xxxxxxxx@sentry.io/xxxxx</div>
                        </li>
                        <li>Restart both services: <code className="text-[10.5px] bg-white/70 rounded px-1 py-0.5">sudo supervisorctl restart backend frontend</code></li>
                    </ol>
                </div>
            )}
        </section>
    );
}

/* -------------------------------------------------------------------------
 * Sprint G G-2 — Email delivery history
 * ------------------------------------------------------------------------- */
function EmailEventsPanel({ events }) {
    if (!events) return null;
    return (
        <section className="mt-10" data-testid="email-events-panel">
            <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                <div className="text-sm font-semibold text-gray-900">Email Delivery History</div>
                {events.failure_count > 0 && (
                    <span className="wfos-pill text-[9px] bg-red-50 text-red-800 border border-red-200">
                        {events.failure_count} failures
                    </span>
                )}
                {events.last_successful_at && (
                    <span className="wfos-pill text-[9px]">
                        Last success: {new Date(events.last_successful_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                )}
            </div>
            {events.events?.length ? (
                <div className="wfos-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-[#fafaf7] text-[11px] uppercase tracking-widest text-gray-500">
                            <tr>
                                <th className="text-left px-4 py-2 font-medium">When</th>
                                <th className="text-left px-4 py-2 font-medium">Kind</th>
                                <th className="text-left px-4 py-2 font-medium">Status</th>
                                <th className="text-left px-4 py-2 font-medium">Detail</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#e8e8e3]">
                            {events.events.map((e) => (
                                <tr key={e.id}>
                                    <td className="px-4 py-2 text-xs text-gray-500">{new Date(e.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                                    <td className="px-4 py-2 text-xs">{e.kind}</td>
                                    <td className="px-4 py-2">
                                        <span className={`wfos-pill text-[10px] ${e.ok ? "wfos-pill--green" : "bg-red-50 text-red-800 border border-red-200"}`}>{e.ok ? "OK" : "FAIL"}</span>
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-600 truncate max-w-[380px]">{e.message}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="wfos-card p-4 text-center text-sm text-gray-400">No email events yet.</div>
            )}
        </section>
    );
}

/* -------------------------------------------------------------------------
 * Sprint G G-8 — Founding letter modal
 * ------------------------------------------------------------------------- */
function FoundingLetterModal({ onClose, onCopy, copied }) {
    return (
        <div
            className="fixed inset-0 bg-black/40 grid place-items-center z-[100] p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="founding-letter-modal"
        >
            <div className="wfos-card w-full max-w-[640px] max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-[#e8e8e3]">
                    <div className="text-[15px] font-semibold text-gray-900">Founding customer offer template</div>
                    <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-[#fafaf7] grid place-items-center text-gray-500">
                        <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                </div>
                <div className="p-4 flex-1 overflow-y-auto wfos-scrollbar">
                    <div className="text-[11.5px] text-gray-500 mb-2">
                        Assign codes to prospects as they say yes — each code is single-use lifetime access. Customize <code>{`{{first_name}}`}</code> per recipient.
                    </div>
                    <pre className="text-[12.5px] text-gray-800 leading-relaxed font-sans whitespace-pre-wrap" data-testid="founding-letter-text">{FOUNDING_CUSTOMER_LETTER}</pre>
                </div>
                <div className="p-4 border-t border-[#e8e8e3] flex justify-end gap-2">
                    <button onClick={onClose} className="rounded-lg border border-[#e8e8e3] px-3 h-9 text-xs text-gray-700">
                        Close
                    </button>
                    <button
                        onClick={onCopy}
                        data-testid="founding-letter-copy"
                        className="inline-flex items-center gap-1 rounded-lg bg-[#16a34a] px-4 h-9 text-xs font-semibold text-white hover:bg-[#15803d]"
                    >
                        {copied ? (
                            <><Check className="w-3.5 h-3.5" strokeWidth={2.5} /> Copied</>
                        ) : (
                            <><Copy className="w-3.5 h-3.5" strokeWidth={2} /> Copy to clipboard</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* --------------------------------------------------------------------------
   Phase 4a polish — Legal Enterprise Pipeline (Owner Console section)
   -------------------------------------------------------------------------- */
function LegalEnterprisePipeline({ inquiries, stats, onStatusChange, updatingId, error }) {
    const LE_STATUS_TONE = (s) => {
        if (s === "new")          return "bg-[#f0fdf4] border-[#bbf7d0] text-[#166534]";
        if (s === "contacted")    return "bg-amber-50 border-amber-200 text-amber-800";
        if (s === "scheduled")    return "bg-blue-50 border-blue-200 text-blue-800";
        if (s === "closed_won")   return "bg-emerald-100 border-emerald-300 text-emerald-800";
        if (s === "closed_lost" || s === "spam") return "bg-gray-50 border-gray-200 text-gray-500";
        return "bg-gray-50 border-gray-200 text-gray-600";
    };
    return (
        <section className="mt-10" data-testid="legal-enterprise-pipeline-section">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#111827] grid place-items-center">
                        <span className="text-white text-[7px] font-bold tracking-widest">LE</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                        Legal Enterprise Pipeline ({stats?.total || 0})
                    </div>
                    <span className="wfos-pill wfos-pill--green text-[9px]">{stats?.new || 0} new</span>
                    <span className="wfos-pill text-[9px]">{stats?.scheduled || 0} scheduled</span>
                    {(stats?.closed_won || 0) > 0 && (
                        <span className="wfos-pill wfos-pill--green text-[9px]">{stats.closed_won} closed-won</span>
                    )}
                </div>
                <div className="text-[11px] text-gray-500">
                    Highest-intent legal buyers. Respond within 1 business day.
                </div>
            </div>

            {error && (
                <div
                    className="mb-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12.5px] text-rose-800"
                    data-testid="legal-inquiries-error"
                >
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" strokeWidth={2} />
                    {typeof error === "string" ? error : "Could not load Legal Enterprise inquiries."}
                </div>
            )}

            {(!inquiries || inquiries.length === 0) ? (
                <div className="wfos-card p-6 text-center text-sm text-gray-500" data-testid="legal-inquiries-empty">
                    <div className="text-2xl font-semibold text-gray-300 mx-auto">LE</div>
                    <div className="mt-1">No Legal Enterprise inquiries yet.</div>
                    <div className="mt-1 text-[11.5px] text-gray-400">
                        Prospects who complete the /legal-enterprise-inquiry wizard land here.
                    </div>
                </div>
            ) : (
                <div className="wfos-card overflow-hidden">
                    <table className="w-full text-sm" data-testid="legal-inquiries-table">
                        <thead className="bg-[#fafaf7] text-[11px] uppercase tracking-widest text-gray-500">
                            <tr>
                                <th className="text-left px-4 py-2 font-medium">Submitted</th>
                                <th className="text-left px-4 py-2 font-medium">Company / Contact</th>
                                <th className="text-left px-4 py-2 font-medium">Size</th>
                                <th className="text-left px-4 py-2 font-medium">Matter vol.</th>
                                <th className="text-left px-4 py-2 font-medium">Spend</th>
                                <th className="text-left px-4 py-2 font-medium">Pain</th>
                                <th className="text-left px-4 py-2 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#e8e8e3]">
                            {inquiries.map((r) => (
                                <tr key={r.id} data-testid={`legal-inquiry-${r.id}`}>
                                    <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap">
                                        {new Date(r.submitted_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </td>
                                    <td className="px-4 py-2">
                                        <div className="text-gray-900 font-medium">{r.company_name}</div>
                                        <div className="text-gray-500 text-xs">
                                            {r.full_name} · <a href={`mailto:${r.work_email}`} className="hover:text-[#16a34a]">{r.work_email}</a>
                                        </div>
                                        {r.role_or_title && (
                                            <div className="text-gray-400 text-[11px]">{r.role_or_title}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-gray-600 text-xs">{r.company_size}</td>
                                    <td className="px-4 py-2 text-gray-800 text-xs font-medium">{r.matter_volume}</td>
                                    <td className="px-4 py-2 text-gray-800 text-xs font-medium">{r.current_legal_spend}</td>
                                    <td className="px-4 py-2 text-gray-600 text-xs">
                                        {(r.primary_pain || "").replace(/_/g, " ")}
                                    </td>
                                    <td className="px-4 py-2">
                                        <select
                                            value={r.status}
                                            disabled={updatingId === r.id}
                                            onChange={(e) => onStatusChange(r.id, e.target.value)}
                                            data-testid={`legal-inquiry-status-${r.id}`}
                                            className={`text-xs rounded-md border px-2 h-7 ${LE_STATUS_TONE(r.status)}`}
                                        >
                                            <option value="new">New</option>
                                            <option value="contacted">Contacted</option>
                                            <option value="scheduled">Scheduled</option>
                                            <option value="closed_won">Closed-won</option>
                                            <option value="closed_lost">Closed-lost</option>
                                            <option value="spam">Spam</option>
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

